import { useCallback, useContext, useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import debounce from 'lodash.debounce';

import { GET_BASIC_SCHEMAS, GET_SCHEMA } from '@atom/graph/schema';
import { Icon, List, Progress, Snackbar, TextField } from '@atom/mui';
import { CloseIcon, SearchIcon } from '@atom/mui/icons';
import { BasicSchema } from '@atom/types/schema';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import CreateAssetModalContext from './CreateAssetModalContext';

import classes from './createAssetModal.module.css';

const { ListItemButton, ListItemText } = List;

const styles = {
  close: {
    cursor: 'pointer',
  },
  list: {
    padding: '0',
    height: '100%',
  },
  listItem: {
    padding: '4px 8px',
  },
  listText: {
    paddingLeft: '0.5rem',
  },
  virtuoso: {
    height: '100%',
  },
};

const MIN_SEARCH_CHARS = 2;
const DEBOUNCE_TIME = 500;

const SchemaSelectionTab = () => {
  const { schema, setSchema } = useContext(CreateAssetModalContext);

  const [basicSchemas, setBasicSchemas] = useState<BasicSchema[]>([]);
  const [searchResultBasicSchemas, setSearchResultBasicSchemas] = useState<
    BasicSchema[]
  >([]);
  const [query, setQuery] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const [getSchema, { loading: loadingHydratedSchema }] = useLazyQuery(
    GET_SCHEMA,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const [getBasicSchemas, { loading: loadingSearchBasicSchemas }] =
    useLazyQuery(GET_BASIC_SCHEMAS, {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    });

  const { data: basicSchemaData, loading: loadingBasicSchemas } = useQuery(
    GET_BASIC_SCHEMAS,
    {
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          rootSchemas: true,
          published: true,
          page,
          limit: 50,
          sortBy: 'name',
          isAscending: true,
        },
      },
    },
  );

  useEffect(() => {
    if (basicSchemaData?.basicSchemas?.basicSchemas) {
      setBasicSchemas(prevSchemas => [
        ...prevSchemas,
        ...basicSchemaData?.basicSchemas?.basicSchemas,
      ]);
    }
  }, [basicSchemaData]);

  const fetchSearchOptionsDebounced = useCallback(
    debounce(async (value: string) => {
      try {
        const { data } = await getBasicSchemas({
          variables: {
            input: {
              name: value,
              rootSchemas: true,
              published: true,
            },
          },
        });
        setSearchResultBasicSchemas(data?.basicSchemas?.basicSchemas || []);
      } catch (error) {
        Snackbar.error({ message: 'An error occurred. Please try again.' });
      }
    }, DEBOUNCE_TIME),
    [],
  );

  const handleQueryChange = (value: string = '') => {
    setQuery(value);

    if (!value) {
      fetchSearchOptionsDebounced.cancel();
    } else if (value.length >= MIN_SEARCH_CHARS) {
      fetchSearchOptionsDebounced(value);
    } else {
      fetchSearchOptionsDebounced.cancel();
    }
  };

  const handleSchemaSelection = async (basicSchema: BasicSchema) => {
    try {
      const { data } = await getSchema({
        variables: {
          id: basicSchema.id,
        },
      });
      setSchema(data?.schema || null);
    } catch (error) {
      Snackbar.error({ message: 'An error occurred. Please try again.' });
    }
  };

  const isSearching = query.length >= MIN_SEARCH_CHARS;
  const noMoreResults =
    basicSchemas.length >= basicSchemaData?.basicSchemas?.totalCount;

  return (
    <div className={classes.tabContainer}>
      {basicSchemas.length === 0 ? (
        <Progress size={20} thickness={2} />
      ) : (
        <>
          <div className={classes.titleContainer}>
            <div className={classes.tabTitle}>Inventory Type</div>
            {(loadingSearchBasicSchemas || loadingHydratedSchema) && (
              <Progress size={20} />
            )}
          </div>
          <TextField
            disabled={loadingHydratedSchema}
            value={query}
            onChange={event => handleQueryChange(event.target.value)}
            data-cy="globalCreateAssetModalSchemaInput"
            placeholder="Search"
            slotProps={{
              input: {
                startAdornment: <Icon icon={SearchIcon} />,
                endAdornment: query && (
                  <Icon
                    style={styles.close}
                    onClick={() => handleQueryChange('')}
                    icon={CloseIcon}
                  />
                ),
              },
            }}
          />
          <div className={classes.listContainer}>
            <List style={styles.list}>
              <Virtuoso
                style={styles.virtuoso}
                data={isSearching ? searchResultBasicSchemas : basicSchemas}
                itemContent={(_, item) => (
                  <ListItemButton
                    disabled={loadingHydratedSchema}
                    key={item.id}
                    onClick={() => handleSchemaSelection(item)}
                    selected={item.id === schema?.id}
                    style={styles.listItem}
                    data-cy={`createAssetSchemaOption-${item.name}`}
                  >
                    {schemaUtilities.getSchemaIconFromSchemaOrAsset(item)}
                    <ListItemText
                      primary={item.name}
                      primaryTextStyle={styles.listText}
                    />
                  </ListItemButton>
                )}
                endReached={() => {
                  if (!noMoreResults) {
                    setPage(prevPage => prevPage + 1);
                  }
                }}
                components={
                  isSearching
                    ? {}
                    : {
                        Footer: () => {
                          if (loadingBasicSchemas) {
                            return <Progress size={20} />;
                          }
                          return null;
                        },
                      }
                }
              />
            </List>
          </div>
        </>
      )}
    </div>
  );
};

export default SchemaSelectionTab;
